import { faExclamationCircle} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { classNames } from '@progress/kendo-react-common';
import { 
    Card, 
    CardBody, 
    CardButtons, 
    CardHeader, 
    CardTitle, 
    DetailsButton, 
    DisputeStepper, 
    AssignButton, 
    CardSubtitle, 
    CardLabel, 
    CardGridAmount, 
    CardGrid } 
    from 'components';
import { formatShortDate } from 'functions';
import { 
    useNavigation, 
    useRefreshDispute, 
    useShowAsEscalated } 
    from 'hooks';
import { useDisputeReasons, useUserInfo } from 'state';
import { DisputeSummary } from 'types';
import { AdjustmentType, AdjustmentTypeText } from "enums";
import styles from './DisputeCard.module.scss';

type DisputeCardProps = {
    disputeSummary: DisputeSummary,
};

const DisputeCard = ({ disputeSummary }: DisputeCardProps) => {
    const { navigateToDispute } = useNavigation();
    const user = useUserInfo();
    const refresh = useRefreshDispute(disputeSummary.disputeNumber);
    const disputeReasons = useDisputeReasons();

    const shouldShowAsEscalated = useShowAsEscalated(disputeSummary);

    return (
        <Card className={classNames(styles.card, { [styles.escalated]: shouldShowAsEscalated })}>
            <CardHeader className={styles.header}>
                <div className={styles.leftDivision}>
                    <CardTitle>
                        <span>Dispute {disputeSummary.disputeNumber}</span>
                    </CardTitle>
                    <div className={styles.headerData}>
                        <CardSubtitle>
                                {disputeSummary.submittedDateUtc && <span><strong>Submitted</strong>: {formatShortDate(new Date(disputeSummary.submittedDateUtc))}</span>}
                        </CardSubtitle>
                        <CardSubtitle>
                                <span><strong>Type</strong>: {AdjustmentTypeText[disputeSummary.adjustmentTypeId as AdjustmentType] ?? disputeSummary.adjustmentTypeId}</span>
                        </CardSubtitle>
                        <CardSubtitle>
                            <span><strong>Disputed Line</strong>: {disputeSummary.disputeFirstItem ?? 'Not Found'}</span>
                        </CardSubtitle>
                    </div>
                </div>
                <div className={styles.rightDivision}>
                    <CardSubtitle>
                     {shouldShowAsEscalated &&
                     <>
                        <CardLabel className={styles.badge}><FontAwesomeIcon className={styles.badge} icon={faExclamationCircle} size="sm" /> Escalated</CardLabel>  
                     </>} 
                    </CardSubtitle>
                </div>
            </CardHeader>
            <CardBody className={styles.body}>
                <CardGrid className={styles.gridTopRow}>
                    <CardSubtitle className={styles.amount}>
                        <CardGridAmount label='Amount' value={disputeSummary.disputeAmount}/>
                    </CardSubtitle>
                    <CardSubtitle className={styles.vendorId}>
                        <div className={styles.label}>Vendor Id</div>
                        <span className={styles.value}>
                            {disputeSummary.vendorId}
                        </span>                    
                    </CardSubtitle>
                    <CardSubtitle className={styles.vendorName}>
                        <div className={styles.label}>Vendor Name</div>
                        <span className={styles.value}>
                            {disputeSummary.vendorName}
                        </span>
                    </CardSubtitle>
                    <CardSubtitle className={styles.disputeReason}>
                        <div className={styles.label}>Dispute Reason</div>
                        <span className={styles.value}>
                            {disputeReasons.find((reason) => reason.disputeReasonId === disputeSummary.disputeReasonId)?.description ?? 'Not Found'}
                        </span>
                    </CardSubtitle>
                </CardGrid>
                <CardGrid className={styles.gridBottomRow}>
                    <div className={styles.dataContainer}>
                        {user.isInternalUser && (
                            <CardSubtitle className={styles.assignedTo}>
                                <div className={styles.label}>  Assigned To</div>
                                <span className={styles.value}>
                                    {disputeSummary?.assigneeEmail
                                        ? `${disputeSummary.assigneeFirstName} ${disputeSummary.assigneeLastName}`
                                        : <span className={styles.unassignedValue}>Unassigned &nbsp;<br /> <AssignButton disputeId={disputeSummary.disputeNumber} onClick={refresh} /></span>
                                    }
                                </span>
                            </CardSubtitle>
                        )}
                        <DisputeStepper 
                            className={user.isInternalUser ? styles.stepperAssignTo : styles.stepperNoAssignTo } 
                            disputeStatus={disputeSummary.disputeStatusId} />                    
                    </div>
                    <CardButtons>
                        <DetailsButton onClick={() => navigateToDispute(disputeSummary.disputeNumber)} />
                    </CardButtons>
                </CardGrid> 
            </CardBody>
        </Card>
    );
};

export default DisputeCard;
